import { Building2, CalendarDays, Radio } from "lucide-react";

import {
  DASHBOARD_CONTEXT_LABELS,
  type DashboardContextEntry,
  type DashboardContextKind,
} from "@/lib/dashboard/contextEntries";

const ICONS = { tenant: Building2, event: CalendarDays, protected_event: Radio } as const;

const HINTS: Record<DashboardContextKind, string> = {
  tenant: "The tenant this dashboard is about — independent of the Tenant filter.",
  event: "The event this dashboard is about.",
  protected_event: "The protected event this dashboard is about.",
};

/**
 * What the dashboard is currently about (its tenant, event, protected event),
 * as plain captioned text beside the title. It is deliberately not a control:
 * the bordered pills in the header's scope cluster filter or select; this only
 * says where you are, so the Tenant filter is never mistaken for it.
 */
export function DashboardContextLine({ entries }: { entries: readonly DashboardContextEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <div
      data-dashboard-context
      role="group"
      aria-label="Dashboard context"
      className="flex min-w-0 items-center gap-x-3"
    >
      {entries.map((entry) => {
        const Icon = ICONS[entry.kind];
        return (
          <span
            key={entry.kind}
            data-context-kind={entry.kind}
            title={HINTS[entry.kind]}
            className="inline-flex min-w-0 items-center gap-1.5 border-l border-border pl-3 first:border-l-0 first:pl-0"
          >
            <Icon className="size-3.5 shrink-0 text-muted" strokeWidth={1.8} aria-hidden />
            <span className="shrink-0 text-muted">{DASHBOARD_CONTEXT_LABELS[entry.kind]}</span>
            <span className="truncate font-medium text-primary">{entry.value}</span>
          </span>
        );
      })}
    </div>
  );
}
